import { PlayerScenarioRunnerAPI } from "@manager/playerManager/PlayerManager";
import { inject, injectable } from "inversify";
import { VintomError } from "../../api/model/error/VintomError";
import { ScenarioElement } from "../../model/playerConfiguration/ScenarioElement";
import { Subtitle } from "../../model/playerConfiguration/Subtitle";
import { PlaybackData } from "../../model/playerState/PlaybackData";
import { ScenarioElementPlayOptions } from "../../model/scenarioElementPlayOptions/ScenarioElementPlayOptions";
import { ScenarioGlobalState } from "../../model/scenarioGlobalState/ScenarioGlobalState";
import { TYPES } from "../di/types";
import { ResourcesCache } from "../loader/scenarioLoader/ResourcesCache";
import { PreferencesStorage } from "../preferencesStorage/PreferencesStorage";
import { ControlsLayerManager } from "./layerManager/ControlsLayerManager";
import { CTALayerManager } from "./layerManager/CTALayerManager";
import { PlayerContainerManager } from "./layerManager/PlayerContainerManager";
import { PosterLayerManager } from "./layerManager/PosterLayerManager";
import { VideoLayerManager } from "./layerManager/VideoLayerManager";
import { ScenarioExecutor } from "./ScenarioExecutor";
import { PlayerMusicManager } from "../scenarioRunner/musicManager/PlayerSyncedMusicManager";

@injectable()
export class CTAOnlyScenarioExecutorImpl implements ScenarioExecutor {
  private playerAPI: PlayerScenarioRunnerAPI;
  private scenarioGlobalState: ScenarioGlobalState;

  constructor(
    @inject(TYPES.scenarioRunner.layerManager.PlayerContainerManager) private playerContainerManager: PlayerContainerManager,
    @inject(TYPES.scenarioRunner.layerManager.CTALayerManager) private ctaLayerManager: CTALayerManager,
    @inject(TYPES.scenarioRunner.layerManager.ControlsLayerManager) private controlsLayerManager: ControlsLayerManager,
    @inject(TYPES.scenarioRunner.layerManager.PosterLayerManager) private posterLayerManager: PosterLayerManager,
    @inject(TYPES.scenarioRunner.layerManager.VideoLayerManager) private videoLayerManager: VideoLayerManager,
    @inject(TYPES.music.MusicManager) private playerMusicManager: PlayerMusicManager,
    @inject(TYPES.preferencesStorage.PreferencesStorage) private preferencesStorage: PreferencesStorage,
  ) {}

  setPlayerAPIDelegate(playerAPI: PlayerScenarioRunnerAPI) {
    this.playerAPI = playerAPI;
  }

  cancel(): void {}

  intializeAndRun(
    scenarioGlobalState: ScenarioGlobalState,
    scenarioResources: Promise<ResourcesCache>,
    options: ScenarioElementPlayOptions,
  ): Promise<void> {
    this.scenarioGlobalState = scenarioGlobalState;
    return this.initializeScenarioElement(scenarioGlobalState, scenarioResources).then(() => {
      const scenarioElement = this.scenarioGlobalState.scenarioPath.currentElement;
      if (options.isAutoplay && !scenarioElement.isCTAOnlyPlaybackStoppingElement) {
        this.controlsLayerManager.play();
      }
    });
  }

  getPlaybackData(): PlaybackData {
    const isPlaying = true;
    const time = 0;
    const duration = 0;
    return { isPlaying, time, duration };
  }

  getScenarioGlobalState(): ScenarioGlobalState {
    return this.scenarioGlobalState;
  }

  play(): void {}

  pause(): void {}

  performAction(ctaId: number, actionId: number, ...args: any[]): void {
    this.ctaLayerManager.performAction(ctaId, actionId, args);
  }

  jumpToScenarioElement(scenarioElement: ScenarioElement, progress: number, isAutoplay: boolean): void {
    this.playerAPI.loadScenarioElement(scenarioElement, {
      isAutoplay,
      progress,
    });
  }

  private initializeScenarioElement(scenarioGlobalState: ScenarioGlobalState, scenarioResources: Promise<ResourcesCache>): Promise<void> {
    this.resetObservers();

    this.videoLayerManager.hide();
    this.ctaLayerManager.newScenarioElement(scenarioGlobalState);

    const scenarioPath = scenarioGlobalState.scenarioPath;
    this.posterLayerManager.newScenarioElement(scenarioGlobalState, 0);
    this.controlsLayerManager.newScenarioElement(scenarioPath);
    this.ctaLayerManager.newScenarioElement(scenarioGlobalState);

    return scenarioResources.then((resourcesCache) => {
      this.ctaLayerManager.resourcesLoaded(resourcesCache, undefined);
      this.ctaLayerManager.displayAllCTAsForCTAOnlyElemnt();
      this.controlsLayerManager.hideLoader();
    });
  }

  private resetObservers() {
    this.videoLayerManager.registerObserver(undefined);

    this.playerContainerManager.registerObserver({
      onPlayerInteractionEnd: () => this.controlsLayerManager.userInteractionEnded(),
      onPlayerInteraction: () => this.controlsLayerManager.userInteractedWithPlayer(),
      onPlayerClick: () => this.controlsLayerManager.userClickedPlayer(),
      onPlayerContextMenu: (x: number, y: number) => this.controlsLayerManager.showPlayerInfoTooltip(x, y),
    });

    this.controlsLayerManager.registerObserver({
      onToggleDebugMode: () => this.playerContainerManager.toggleDebugMode(),
      onPause: () => {},
      onSeekStart: () => {},
      onPlay: () => {
        const scenarioElement = this.scenarioGlobalState.scenarioPath.currentElement;
        if (!scenarioElement.isCTAOnlyPlaybackStoppingElement) {
          this.playerAPI.loadNextScenarioElement();
        }
      },
      onReloadFrame: () => {},
      onSeekTo: (time) => {},
      onVolumeChange: (volume: number) => {
        this.preferencesStorage.saveVolumePreference(volume);
        this.scenarioGlobalState = this.scenarioGlobalState.withVolume(volume);
      },
      onMutedChange: (isMuted: boolean) => {
        this.scenarioGlobalState = this.scenarioGlobalState.withMuted(isMuted);
      },
      onSubtitleSourceSelected: (subtitle: Subtitle) => {},
      onJumpToScenarioElement: (scElement: ScenarioElement, progress: number, isAutoplay?: boolean) =>
        this.jumpToScenarioElement(scElement, progress, isAutoplay),
      onSendError: (error: VintomError) => this.playerAPI.logUserError(error),
      onToggleFullscreen: () => this.playerContainerManager.toggleFullscreen(),
    });

    this.ctaLayerManager.registerObserver({
      onCTAShow: (cta) => this.playerAPI.onCTAShow(cta),
    });
  }
}
